import Vue from 'vue'
import VueRouter from 'vue-router'
import { applyRouteSeo } from '../services/seo'
import { trackPageView } from '../services/analytics'

Vue.use(VueRouter)

const noindexSeo = {
  title: 'CashBoomerang Account',
  description: 'Secure CashBoomerang account page.',
  robots: 'noindex,nofollow'
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/Home.vue'),
    meta: {
      seo: {
        title: 'CashBoomerang | Cash Back Guides and Curated Shopping Deals',
        description: 'Learn how online cash back works, compare eligible shopping offers, and browse clearly disclosed affiliate deals with CashBoomerang.',
        canonical: '/'
      }
    }
  },
  {
    path: '/how-cashback-works',
    name: 'HowCashbackWorks',
    component: () => import('../views/HowCashbackWorks.vue'),
    meta: {
      seo: {
        title: 'How Cash Back Works: From Click to Payout | CashBoomerang',
        description: 'Understand cash-back eligibility, tracked shopping trips, pending rewards, retailer approval, reversals, and payouts in three clear steps.',
        canonical: '/how-cashback-works',
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'How to earn cash back with an eligible CashBoomerang offer',
          description: 'Choose an eligible offer, activate the tracked shopping trip, and wait for retailer approval.',
          step: [
            { '@type': 'HowToStep', position: 1, name: 'Choose an eligible offer', text: 'Select an offer explicitly marked Cash Back Eligible and read its terms.' },
            { '@type': 'HowToStep', position: 2, name: 'Activate and shop', text: 'Sign in and use the activation button before completing the purchase in the same browser session.' },
            { '@type': 'HowToStep', position: 3, name: 'Wait for confirmation', text: 'The reward remains pending until the retailer approves it after the return or cancellation period.' }
          ]
        }
      }
    }
  },
  {
    path: '/cashback-guide',
    name: 'CashbackGuide',
    component: () => import('../views/CashbackGuide.vue'),
    meta: {
      seo: {
        title: 'Online Cash Back Guide: Eligibility and Tracking | CashBoomerang',
        description: 'A practical guide to online cash back: how commissions fund rewards, how transaction tracking works, and why a reward may be missing or pending.',
        canonical: '/cashback-guide'
      }
    }
  },
  {
    path: '/faq',
    name: 'FAQ',
    component: () => import('../views/FAQ.vue'),
    meta: {
      seo: {
        title: 'Cash Back and Affiliate Shopping FAQ | CashBoomerang',
        description: 'Answers about eligible cash-back offers, Amazon affiliate links, missing transactions, pending rewards, reversals, reward calculation, and payouts.',
        canonical: '/faq',
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'Is every product on CashBoomerang eligible for cash back?', acceptedAnswer: { '@type': 'Answer', text: 'No. Only offers explicitly marked Cash Back Eligible can earn a reward. Other links may be disclosed affiliate shopping links.' } },
            { '@type': 'Question', name: 'Do Amazon purchases earn CashBoomerang cash back?', acceptedAnswer: { '@type': 'Answer', text: 'No. Amazon Associates links may generate a commission for CashBoomerang, but they are not eligible for CashBoomerang user cash back.' } },
            { '@type': 'Question', name: 'Why is my reward pending?', acceptedAnswer: { '@type': 'Answer', text: 'The retailer must validate the purchase and wait for cancellation or return risks to pass. Timing varies by retailer and purchase type.' } },
            { '@type': 'Question', name: 'Can a reward be reversed?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Returns, cancellations, refunds, duplicate orders, exclusions, or retailer rejection can reverse a reward.' } }
          ]
        }
      }
    }
  },
  {
    path: '/product/:category/:id',
    name: 'ProductDetail',
    component: () => import('../views/ProductDetail.vue'),
    meta: { seo: { ...noindexSeo, title: 'Product Shopping Guide | CashBoomerang', canonical: null } }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue'),
    meta: { seo: { title: 'About CashBoomerang | Transparent Shopping Rewards', description: 'Learn why CashBoomerang is building a transparent shopping-rewards platform and how we distinguish eligible cash back from disclosed affiliate deals.', canonical: '/about' } }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('../views/Terms.vue'),
    meta: { seo: { title: 'Terms of Service | CashBoomerang', description: 'Read the CashBoomerang terms for accounts, eligible rewards, transaction tracking, exclusions, reversals, third-party retailers, and service use.', canonical: '/terms' } }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('../views/Privacy.vue'),
    meta: { seo: { title: 'Privacy Policy | CashBoomerang', description: 'Learn what information CashBoomerang collects, why it is used, how analytics and account data are handled, and the choices available to visitors.', canonical: '/privacy' } }
  },
  { path: '/login', name: 'Login', component: () => import('../views/Login.vue'), meta: { seo: { ...noindexSeo, title: 'Log In | CashBoomerang' } } },
  { path: '/signup', name: 'Signup', component: () => import('../views/Signup.vue'), meta: { seo: { ...noindexSeo, title: 'Create an Account | CashBoomerang' } } },
  { path: '/dashboard', name: 'Dashboard', component: () => import('../views/Dashboard.vue'), meta: { requiresAuth: true, seo: { ...noindexSeo, title: 'Rewards Dashboard | CashBoomerang' } } },
  { path: '/admin', name: 'Admin', component: () => import('../views/Admin.vue'), meta: { requiresAuth: true, requiresAdmin: true, seo: { ...noindexSeo, title: 'Admin | CashBoomerang' } } },

  // Old PPE pages belonged to the starter template. Firebase Hosting performs the real 301 redirects.
  { path: '/gafas', redirect: '/' },
  { path: '/guantes', redirect: '/' },
  { path: '/desinfectantes', redirect: '/' },
  { path: '/otros', redirect: '/' },
  { path: '/infantiles', redirect: '/' },
  { path: '/medicas', redirect: '/' },
  { path: '/FFP3-N95-FFP2', redirect: '/' },
  {
    path: '*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue'),
    meta: { seo: { ...noindexSeo, title: 'Page Not Found | CashBoomerang' } }
  }
]

const router = new VueRouter({ mode: 'history', routes })

router.afterEach(to => {
  Vue.nextTick(() => {
    applyRouteSeo(to)
    trackPageView(to)
  })
})

export default router
